import React, { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';

import { useDeepCompareMemo } from '@clatter/platform';
import { useGetSubFoldersQuery, useUpdateAssetMutation, useUpdateFolderMutation } from '../../services';
import {
  FolderManagerAction,
  FolderManagerActionTypes,
  FolderManagerItemTypes,
  FolderManagerViewTypes,
} from '../../interfaces';
import { useAssetDownload, useFolderManager } from '../../hooks';
import { FolderManager } from '../../components';
import { ProjectPageLayout } from '../../layouts';
import { addNotification, setNewButtonState, assetManagerUISelectors } from '../../store';

import { FolderManagerDialog } from '../../components/FolderManager/Components/FolderManagerDialog';
import { FolderManagerShareDialog } from '../../components/FolderManager/Components/FolderManagerShareDialog';
import { FolderManagerMoveDialog } from '../../components/FolderManager/Components/FolderManagerMoveDialog';

interface DocumentsPageProps {
  rootFolderId?: number;
}

const initialRenameDialogState: IRenameDialogState = {
  opened: false,
  actionType: null,
  editedItem: null,
};

const shareDialogInitialState = {
  opened: false,
  selectedItems: [],
};

const initialMoveDialogState: IMoveDialogState = {
  opened: false,
  rootFolder: null,
  selectedItems: [],
};

const DocumentsPage: FC<DocumentsPageProps> = ({ rootFolderId: folderIdFromParent }) => {
  const ctx = useOutletContext<{ isLoading: boolean; rootFolderId: number } | undefined>();

  const { rootFolderId: folderIdFromContext, isLoading: isContextLoading } = ctx || {};

  const dispatch = useDispatch();

  // Fallback: resolve uploadedData folder directly when not provided via outlet context
  const entitiesSidebarItem = useSelector((state) => assetManagerUISelectors.getSidebarItemBySlug(state, 'entities'));
  const { data: entitiesSubFolders, isLoading: isEntitiesSubFoldersLoading } = useGetSubFoldersQuery(
    { parentId: entitiesSidebarItem?.id },
    { skip: !!folderIdFromContext || !!folderIdFromParent || !entitiesSidebarItem?.id },
  );
  const uploadedDataFolderIdFallback = useDeepCompareMemo(
    () => (entitiesSubFolders || []).find((item: any) => item?.displayName?.toLowerCase() === 'uploaded data')?.id,
    [entitiesSubFolders],
  );
  const { downloadAssetById } = useAssetDownload();

  const [updateFolder] = useUpdateFolderMutation();
  const [updateAsset] = useUpdateAssetMutation();

  const newButtonState = useSelector(assetManagerUISelectors.getNewButtonState);

  const rootFolderId = folderIdFromContext || folderIdFromParent || uploadedDataFolderIdFallback;

  const {
    isLoading: isFolderManagerLoading,
    breadcrumbs,
    selectedFolderId,
    hierarchyData: folderManagerHierarchyData,
    handleFolderClick,
  } = useFolderManager({ rootFolderId: rootFolderId!, inMemory: true });

  const isLoading = isFolderManagerLoading || !!isContextLoading || isEntitiesSubFoldersLoading;

  const [moveDialogState, setMoveDialogState] = useState(initialMoveDialogState);
  const [shareDialogState, setShareDialogState] = useState(shareDialogInitialState);
  const [renameDialogState, setRenameDialogState] = useState(initialRenameDialogState);

  const breadcrumbsWithoutWorkspaceRoot = useDeepCompareMemo(() => {
    return (breadcrumbs || []).slice(1);
  }, [breadcrumbs]);

  const handleFolderManagerAction = useCallback(
    async (action: FolderManagerAction) => {
      if (!action) {
        console.error('No action provided');
        return;
      }

      const { actionType, payload = {} } = action;
      const { id, row, selectedItems = [], selectedRows = [], item } = payload;

      switch (actionType) {
        case FolderManagerActionTypes.GET_SUB_FOLDERS:
          if (id) {
            await handleFolderClick({ item: payload as FolderManagerItem });
          } else {
            console.error(`${FolderManagerActionTypes.GET_SUB_FOLDERS}:: Missing ID in the payload`);
          }
          break;

        case FolderManagerActionTypes.RENAME:
          setRenameDialogState({
            opened: true,
            editedItem: payload,
            actionType,
          });
          break;

        case FolderManagerActionTypes.SHARE:
          setShareDialogState({
            opened: true,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            selectedItems: [row, ...selectedItems, ...selectedRows, item].filter(Boolean),
          });
          break;

        case FolderManagerActionTypes.DOWNLOAD:
          if (!id || Number.isNaN(id)) {
            dispatch(
              addNotification({
                type: 'error',
                message: 'Couldn\'t download this file. Please try again.',
              }),
            );
            break;
          }

          await downloadAssetById({ assetId: id });

          break;

        case FolderManagerActionTypes.MOVE:
          // handle one item move
          if (payload?.item) {
            setMoveDialogState({
              opened: true,
              selectedItems: [payload.item],
              rootFolder: { id: rootFolderId } as FolderManagerItem,
            });
            return;
          }

          if (payload?.selectedItems) {
            setMoveDialogState({
              opened: true,
              selectedItems: payload.selectedItems,
              rootFolder: { id: rootFolderId } as FolderManagerItem,
            });
          }
          break;

        case FolderManagerActionTypes.DELETE:
          const itemsToDelete: FolderManagerItem[] = payload?.selectedItems ? payload?.selectedItems : [payload];

          // move to trash
          // this check is not ideal we should consider passing some prop in the payload...
          if (
            window.confirm(
              `Move ${
                itemsToDelete.length > 1 ? itemsToDelete?.length + ' items' : itemsToDelete?.[0]?.name
              } to trash?`,
            )
          ) {
            let deletePromises: Promise<any>[] = [];

            for (const itemToDelete of itemsToDelete) {
              if (itemToDelete?.type === FolderManagerItemTypes.FOLDER) {
                deletePromises.push(
                  updateFolder({
                    folderId: itemToDelete.id,
                    data: { move_to_trash: true },
                  }),
                );
              } else {
                deletePromises.push(
                  updateAsset({
                    assetId: itemToDelete.id,
                    data: { move_to_trash: true },
                  }),
                );
              }
            }

            if (deletePromises?.length) {
              try {
                await Promise.all(deletePromises);
              } catch (error: any) {
                console.error('Error deleting item', error);
              }
            }
          }

          break;

        default:
          console.error(`Unsupported action type: ${actionType}`);
      }
    },
    [handleFolderClick, dispatch],
  );

  const handleMoveDialogDismiss = (data: IMoveDialogOnDismiss | null) => {
    //@todo handle success | error
    // close dialog
    setMoveDialogState(initialMoveDialogState);
  };

  if (!rootFolderId) {
    console.warn('Could not load documents data no root folder ID provided!');
    return null;
  }

  return (
    <ProjectPageLayout noWrapper>
      <FolderManager
        isLoading={isLoading}
        breadcrumbs={breadcrumbsWithoutWorkspaceRoot}
        rootFolderId={rootFolderId}
        hierarchyData={folderManagerHierarchyData}
        newButtonActionType={newButtonState}
        selectedFolderId={selectedFolderId}
        defaultViewType={FolderManagerViewTypes.LIST}
        onClearNewButtonAction={() => dispatch(setNewButtonState(null))}
        onAction={handleFolderManagerAction}
      />

      {shareDialogState.opened && (
        <FolderManagerShareDialog
          opened={shareDialogState.opened}
          selectedRows={shareDialogState.selectedItems}
          onDismiss={() => setShareDialogState(shareDialogInitialState)}
        />
      )}

      {renameDialogState?.opened && (
        <FolderManagerDialog
          isLoading={isLoading}
          dialogState={renameDialogState}
          onDismiss={() => setRenameDialogState(initialRenameDialogState)}
        />
      )}

      {moveDialogState?.opened && (
        <FolderManagerMoveDialog dialogState={moveDialogState} onDismiss={handleMoveDialogDismiss} />
      )}
    </ProjectPageLayout>
  );
};

export default DocumentsPage;
